/**
 * The stats view's "tokens per repo" chart: what the box's Claudes spent at
 * each point, stacked by the repo they worked in, with use off the box on top.
 *
 * The repos are measured (runner/localUsage.ts). Use elsewhere is not: it is
 * only ever known as points of the five-hour window (`outsideRise()`), and
 * tokens and percent have no documented rate. So it is drawn at the rate THIS
 * RANGE shows — the box's tokens over the points the box accounts for — and
 * said to be an estimate. With nothing to work a rate out from, it is left out.
 *
 * A repo's colour is its slot, and its slot comes from the server's list of
 * every repo it holds tokens for, which does not change with the range: the
 * biggest `REPO_SLOTS` get one, in name order, so a repo keeps its colour from
 * 6h to 30d. The rest are one grey band.
 */
import type { StatsHistory } from "@shared/types.js";

/** How many repos get a colour of their own (`--repo-1`… in styles.css). */
export const REPO_SLOTS = 6;
/** Fewer five-hour points than this, and the range says nothing about what a point costs. */
const RATE_MIN_POINTS = 1;

export interface TokenSeries {
  key: string;
  label: string;
  /** Its class: `repo-1`…, `other`, `unsplit` or `outside`. */
  slot: string;
  /** Tokens at each point of `TokenStack.at`. */
  values: number[];
  total: number;
  /** Worked out, not measured. */
  estimate: boolean;
}

export interface TokenStack {
  at: number[];
  /** Bottom of the stack first; only those with anything in the range. */
  series: TokenSeries[];
  /** Every series at each point, added up. */
  totals: number[];
  /** Tokens to a point of the five-hour window over this range, or null with too little to say. */
  rate: number | null;
}

const repoLabel = (repo: string): string => repo || "no repo";

/** Which slot each repo draws in; one not in it folds into "other". */
export function repoSlots(repos: string[]): Map<string, string> {
  const named = [...repos.slice(0, REPO_SLOTS)].sort((a, b) => a.localeCompare(b));
  return new Map(named.map((repo, i) => [repo, `repo-${i + 1}`]));
}

export function tokenStack(h: StatsHistory): TokenStack {
  // A server older than this chart sends no tokens at all.
  const rows = h.usage.filter((r) => typeof r.tokens === "number");
  const slots = repoSlots(h.repos ?? []);
  const blank = (): number[] => rows.map(() => 0);

  let tokens = 0;
  let points = 0;
  for (const r of rows) {
    if (r.used === null) continue;
    tokens += r.tokens!;
    points += Math.max(0, r.used - r.outside);
  }
  const rate = tokens > 0 && points >= RATE_MIN_POINTS ? tokens / points : null;

  const bySlot = new Map<string, TokenSeries>();
  const series = (key: string, label: string, slot: string, estimate = false): TokenSeries => {
    let s = bySlot.get(key);
    if (!s) bySlot.set(key, (s = { key, label, slot, values: blank(), total: 0, estimate }));
    return s;
  };
  // In stack order, whether or not the range has any: slots, then the greys, then elsewhere.
  for (const [repo, slot] of slots) series(`repo:${repo}`, repoLabel(repo), slot);
  const other = series("other", "other repos", "other");
  const unsplit = series("unsplit", "not split by repo", "unsplit");
  const outside = series("outside", "elsewhere", "outside", true);

  rows.forEach((r, i) => {
    let split = 0;
    for (const [repo, n] of Object.entries(r.repos ?? {})) {
      split += n;
      const s = slots.has(repo) ? bySlot.get(`repo:${repo}`)! : other;
      s.values[i]! += n;
    }
    // A reading from a runner that sent only the total.
    unsplit.values[i] = Math.max(0, r.tokens! - split);
    if (rate !== null) outside.values[i] = r.outside * rate;
  });

  const kept = [...bySlot.values()].filter((s) => {
    s.total = s.values.reduce((a, b) => a + b, 0);
    return s.total > 0;
  });
  return { at: rows.map((r) => r.at), series: kept, totals: rows.map((_, i) => kept.reduce((n, s) => n + s.values[i]!, 0)), rate };
}
